import { useEffect, useMemo } from "react";
import { useDialogModal } from "@/hook/handle-modal/hooks/actions";
import { isValidPeriod } from "@/utils/date-range";
import { OnChangeFn, SortingState } from "@tanstack/react-table";
import { ContentContainer, PageLayout, PageTitle } from "components/layout";
import {
  CalendarRange,
  CheckCircle2,
  Clock,
  RefreshCw,
  Wallet,
} from "lucide-react";
import { Tags } from "@/enum/tags";
import { useManualRefresh } from "@/hooks/use-manual-refresh";
import { voucherColumns } from "./columns";
import { CycleBanner } from "./components/cycle-banner";
import { DataTable } from "./components/data-table";
import { DataTablePagination } from "./components/data-table-pagination";
import { FiscalCycleExportModal } from "./components/fiscal-cycle-export-modal";
import { toVoucherSorting, toVoucherSortingState } from "./sorting";
import { DateRangeInputs } from "./components/date-range-inputs";
import { ExportTablesMenu } from "./components/export-tables-menu";
import { StatusChips } from "./components/status-chips";
import { VoucherCards } from "./components/voucher-cards";
import {
  ALL_STATUS_FILTER,
  EXPORT_ITEM_LABELS,
  PAGE_DESCRIPTION,
  PAGE_TITLE,
} from "./constants";
import { usePayrollExport } from "./hooks/use-payroll-export";
import { useVoucherListParams } from "./hooks/use-voucher-list-params";
import { useVoucherStatusCounts } from "./hooks/use-voucher-status-counts";
import { useVouchersSource } from "./hooks/use-vouchers-source";
import { PAYROLL_VOUCHER_LABELS } from "./messages";
import { PayrollVoucher, StatusFilterKey, VoucherDecision } from "./types";
import { purgeLegacyVoucherDrafts } from "./legacy-drafts";

const PayrollVoucherApproval = (): JSX.Element => {
  useEffect((): void => purgeLegacyVoucherDrafts(), []);

  const {
    range,
    statusFilter,
    page,
    perPage,
    sorting,
    setRange,
    setStatusFilter,
    setPagination,
    setSorting,
  } = useVoucherListParams();

  const { vouchers, meta, cycle, isFetching, decide, decideBatch } =
    useVouchersSource({
      range,
      page,
      perPage,
      status: statusFilter === ALL_STATUS_FILTER ? undefined : statusFilter,
      sort: sorting.sort,
      direction: sorting.direction,
    });

  const counts: Record<StatusFilterKey, number> = useVoucherStatusCounts(range);

  const { refresh, refreshedAt } = useManualRefresh([
    Tags.PAYROLL_VOUCHER,
    Tags.PAYROLL_VOUCHER_CYCLE,
  ]);

  const { handleModal } = useDialogModal();

  const sortingState: SortingState = toVoucherSortingState(sorting);

  const handleSortingChange: OnChangeFn<SortingState> = (updater): void => {
    const next: SortingState =
      typeof updater === "function" ? updater(sortingState) : updater;

    setSorting(toVoucherSorting(next));
  };

  const handleDecide = async (
    voucher: PayrollVoucher,
    decision: VoucherDecision,
  ): Promise<void> => {
    const accepted: boolean = await decide(voucher, decision);

    if (!accepted) return;

    handleModal({ isOpen: false });
  };

  const handleDecideBatch = async (
    selected: readonly PayrollVoucher[],
    decision: VoucherDecision,
  ): Promise<boolean> => {
    const accepted: boolean = await decideBatch(selected, decision);

    if (!accepted) return false;

    handleModal({ isOpen: false });

    return true;
  };

  const { exportRange, exportFiscalCycle } = usePayrollExport();

  const canExportRange: boolean = isValidPeriod(range.start, range.end);

  // O mês escolhido no seletor virava nada: `onSelect` descartava os dois
  // argumentos e a API decidia sozinha o ciclo. Agora o rótulo `AAAA-MM` viaja
  // até a rota (WI 12811).
  const confirmFiscalCycleExport = (year: number, month: number): void => {
    const chosenCycle: string = `${year}-${String(month).padStart(2, "0")}`;

    handleModal({
      isOpen: true,
      title: PAYROLL_VOUCHER_LABELS.EXPORT_CLOSES_PERIOD_TITLE,
      element: (
        <FiscalCycleExportModal
          cycle={chosenCycle}
          onConfirm={async (): Promise<void> => {
            await exportFiscalCycle(chosenCycle);
            handleModal({ isOpen: false });
          }}
        />
      ),
    });
  };

  const rows: PayrollVoucher[] = useMemo(
    (): PayrollVoucher[] => [...vouchers],
    [vouchers],
  );

  return (
    <PageLayout>
      <div className="flex w-full flex-1 flex-wrap items-start justify-between gap-3">
        <PageTitle title={PAGE_TITLE} description={PAGE_DESCRIPTION} />
        <div className="cursor-pointer">
          <DateRangeInputs value={range} onChange={setRange} />
        </div>
      </div>

      <ContentContainer className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={refresh}
              disabled={isFetching}
              className="flex items-center gap-1.5 text-sm text-[#3c7dd9] hover:underline disabled:cursor-not-allowed disabled:text-gray-400"
            >
              <RefreshCw
                className={`h-3.5 w-3.5 ${isFetching ? "animate-spin" : ""}`}
              />
              {isFetching
                ? PAYROLL_VOUCHER_LABELS.RELOADING
                : PAYROLL_VOUCHER_LABELS.RELOAD}
            </button>
            {refreshedAt && !isFetching && (
              <span className="text-xs text-muted-foreground">
                {PAYROLL_VOUCHER_LABELS.REFRESHED_AT(refreshedAt)}
              </span>
            )}
          </div>

          <ExportTablesMenu
            items={[
              {
                key: "payroll-discount",
                label: EXPORT_ITEM_LABELS.PAYROLL_DISCOUNT,
                disabled: !canExportRange,
                disabledReason:
                  PAYROLL_VOUCHER_LABELS.EXPORT_RANGE_REQUIRES_PERIOD,
                onSelect: (): void => exportRange(range),
              },
            ]}
            currentCycle={cycle.cycle}
            consolidatedItems={[
              {
                key: "consolidated-payroll-discount",
                label: EXPORT_ITEM_LABELS.CONSOLIDATED_PAYROLL_DISCOUNT,
                onSelect: (year: number, month: number): void =>
                  confirmFiscalCycleExport(year, month),
              },
            ]}
          />
        </div>

        <CycleBanner
          items={[
            {
              label: PAYROLL_VOUCHER_LABELS.CYCLE_OPEN,
              value: cycle.interval,
              Icon: CalendarRange,
            },
            {
              label: PAYROLL_VOUCHER_LABELS.DAYS_TO_CLOSE,
              value: PAYROLL_VOUCHER_LABELS.DAYS(cycle.days_to_close),
              Icon: Clock,
            },
            {
              label: PAYROLL_VOUCHER_LABELS.VOUCHERS_IN_CYCLE,
              value: `${cycle.vouchers_in_cycle}`,
              detail: PAYROLL_VOUCHER_LABELS.DECIDED_PERCENTAGE(
                cycle.decided_percentage,
              ),
              Icon: CheckCircle2,
            },
            {
              label: PAYROLL_VOUCHER_LABELS.PAYROLL_DISCOUNT_IN_CYCLE,
              value: `${cycle.payroll_discount_in_cycle}`,
              detail: PAYROLL_VOUCHER_LABELS.EXPORTED_PERCENTAGE(
                cycle.exported_percentage,
              ),
              Icon: Wallet,
            },
          ]}
        />

        <StatusChips
          value={statusFilter}
          counts={counts}
          onChange={setStatusFilter}
        />

        <DataTable
          columns={voucherColumns}
          data={rows}
          sorting={sortingState}
          onSortingChange={handleSortingChange}
          onDecide={handleDecide}
          onDecideBatch={handleDecideBatch}
        />
        <VoucherCards vouchers={rows} onDecide={handleDecide} />
        <DataTablePagination
          meta={meta}
          page={page}
          perPage={perPage}
          isFetching={isFetching}
          onChange={setPagination}
        />
      </ContentContainer>
    </PageLayout>
  );
};

export default PayrollVoucherApproval;
